import React from 'react';
import './Button.css';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button visual variant */
  variant?: 'primary' | 'secondary' | 'ghost' | 'icon';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Full width button */
  fullWidth?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Pressed/active state (for toggle buttons) */
  pressed?: boolean;
  /** Icon before label */
  iconBefore?: React.ReactNode;
  /** Icon after label */
  iconAfter?: React.ReactNode;
}

/**
 * Primary button component for actions.
 * Follows DES-033 design system with warm, encouraging interactions.
 *
 * Variants:
 * - primary: Main action buttons
 * - secondary: Secondary actions
 * - ghost: Minimal styling
 * - icon: Circular icon-only buttons (for toolbars)
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      pressed = false,
      iconBefore,
      iconAfter,
      className = '',
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const classes = [
      'stc-btn',
      `stc-btn--${variant}`,
      `stc-btn--${size}`,
      fullWidth && 'stc-btn--full-width',
      loading && 'stc-btn--loading',
      pressed && 'stc-btn--pressed',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // For icon variant, don't wrap in label span
    if (variant === 'icon') {
      return (
        <button
          ref={ref}
          className={classes}
          disabled={disabled || loading}
          aria-pressed={pressed ? 'true' : undefined}
          {...props}
        >
          {children}
        </button>
      );
    }

    return (
      <button ref={ref} className={classes} disabled={disabled || loading} {...props}>
        {loading && <span className="stc-btn__spinner" aria-hidden="true" />}
        {!loading && iconBefore && <span className="stc-btn__icon">{iconBefore}</span>}
        <span className="stc-btn__label">{children}</span>
        {!loading && iconAfter && <span className="stc-btn__icon">{iconAfter}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';

export interface ButtonLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Button visual variant */
  variant?: 'primary' | 'secondary' | 'ghost' | 'icon';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Button styled as an anchor link.
 * Use for navigation that should look like a button.
 */
export const ButtonLink = React.forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  ({ variant = 'primary', size = 'md', className = '', children, ...props }, ref) => {
    const classes = ['stc-btn', `stc-btn--${variant}`, `stc-btn--${size}`, className]
      .filter(Boolean)
      .join(' ');

    return (
      <a ref={ref} className={classes} {...props}>
        {variant === 'icon' ? children : <span className="stc-btn__label">{children}</span>}
      </a>
    );
  }
);

ButtonLink.displayName = 'ButtonLink';
